import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getVolunteerReports } from '../../../services/volunteerApi';
import { VolunteerReports } from '../../../types/volunteer';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const VolunteerReportsPage: React.FC = () => {
  const [reports, setReports] = useState<VolunteerReports | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const res = await getVolunteerReports();
        setReports(res.data);
      } catch (err) {
        console.error('Failed to fetch volunteer reports:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!reports) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          Unable to load reports
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Volunteer Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Overview of volunteer activity and engagement
      </Typography>

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Volunteers"
            value={reports.totalVolunteers}
            icon={<PeopleIcon sx={{ color: 'white' }} />}
            color="#00b7ed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Applications"
            value={reports.pendingApplications}
            icon={<PendingActionsIcon sx={{ color: 'white' }} />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Shifts"
            value={reports.activeShifts}
            icon={<EventIcon sx={{ color: 'white' }} />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Hours Logged"
            value={reports.totalHoursLogged.toFixed(1)}
            icon={<AccessTimeIcon sx={{ color: 'white' }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Top Volunteers Table */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Top Volunteers
      </Typography>

      {reports.topVolunteers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">No volunteer data available yet</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total Hours</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total Shifts</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.topVolunteers.map((volunteer, index) => (
                <TableRow key={volunteer.user.id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>{index + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={500}>
                      {volunteer.user.firstName} {volunteer.user.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>{volunteer.user.email}</TableCell>
                  <TableCell>
                    <Typography fontWeight={500}>
                      {volunteer.totalHours.toFixed(1)}h
                    </Typography>
                  </TableCell>
                  <TableCell>{volunteer.totalShifts}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default VolunteerReportsPage;
